import getProviders from './lib/getProviders'
import request from 'api/requestUtils'
import { arrayToObject, groupBy, min } from 'lib/arrayUtils'
import moment from 'moment'
import qs from 'qs'
import { dateIsBefore } from 'lib/dateUtils'
import { getPackageUniqueKey } from 'lib/offerUtils'
import generateOccupancyStringByRoom from 'lib/generateOccupancyStringByRoom'
import availablePriceMap, { ServerAvailablePrice } from './mappers/availablePriceMap'
import { ServerTaxBreakdown, taxBreakdownMap } from './mappers/taxBreakdownMap'
import { MONTH_NAMES } from 'constants/dates'
import { isTaxExclusiveForRegion } from 'lib/isTaxExclusive'
import { mergeObjects } from 'lib/objectUtils'

const FLIGHT_MAX_DAYS_IN_FUTURE = 310
const maxFlightDate = moment().add(FLIGHT_MAX_DAYS_IN_FUTURE, 'days').toDate()

export interface ServerCalendarPrice {
  day: number;
  month: number;
  year: number;
  checkIn: string;
  checkOut: string;
  rates: Array<{
    roomRateId: string;
    taxesAndFees?: number;
    propertyFees?: number;
    accommodationPrice?: number;
    accommodationValue?: number;
    roomsPrices?: number[];
    blackout?: boolean;
    flightPrice?: number;
    surcharge: number;
    extraGuestSurcharge: number;
    availableRooms: number;
  }>;
}

interface ServerCalendar {
  packageId: string;
  uniqueKey: string;
  prices: Array<ServerCalendarPrice>;
  flightCacheActive: boolean;
  flightsNotAvailable: boolean;
}

function mapCalendar(
  serverCalendar: ServerCalendar,
  packages: Array<App.Package>,
  duration: number,
  offer: App.Offer | App.OfferSummary,
  regionCode: string,
): Array<App.Calendar> {
  const flightCacheActive = serverCalendar.flightCacheActive
  const flightsNotAvailable = serverCalendar.flightsNotAvailable
  const offerPackage = packages.find(pkg => pkg.id === serverCalendar.packageId)

  // current issue with server where returns an empty prices array if no avaialble dates
  if (serverCalendar.prices.length === 0) {
    if (offerPackage) {
      return [{
        packageId: serverCalendar.packageId,
        uniqueKey: offerPackage.uniqueKey,
        months: [],
        cheapestDay: undefined,
        cheapestDayWithFlights: undefined,
        flightCacheActive,
        flightsNotAvailable,
      }]
    } else {
      return []
    }
  }

  return (serverCalendar.prices[0].rates as Array<any>).map<App.Calendar>((rate, index) => {
    const pricesByMonth = groupBy(serverCalendar.prices, (price) => `${price.month}-${price.year}`)
    const uniqueKey = getPackageUniqueKey(serverCalendar.packageId, duration, rate.roomRateId)

    const months = [...pricesByMonth.entries()].map<App.CalendarMonth>(([monthKey, prices]) => {
      const monthIndex = prices[0].month - 1
      const monthText = MONTH_NAMES[monthIndex]
      const days = prices.filter(day => day.rates[index]).map<App.CalendarDay>((day) => {
        const dayRate = day.rates[index]
        const flightPrice = dayRate.flightPrice
        let hotelAvailable = !!dayRate.accommodationPrice
        if (offer.bundledWithFlightsOnly) {
          // bundled with flight deals *must* have a flight price if the cache is active
          // otherwise they are considered sold out (because no flight price = no flights)
          hotelAvailable = hotelAvailable && (!flightCacheActive || (flightCacheActive && !!flightPrice))
        }

        const canRequestDates = !hotelAvailable && !dayRate.blackout &&
          !!offerPackage?.allowDatesRequest && dateIsBefore(new Date(day.checkOut), new Date(offer.bookByDate))

        const taxesAndFees = dayRate.taxesAndFees ?? 0
        const propertyFees = dayRate.propertyFees ?? 0
        const isPriceTaxExclusive = isTaxExclusiveForRegion(regionCode)
        const hotelPrice = !hotelAvailable ? 0 : ((dayRate.accommodationPrice ?? 0) - (isPriceTaxExclusive ? taxesAndFees : 0))
        const surcharge = !hotelAvailable ? 0 : (dayRate.surcharge ?? 0)
        const extraGuestSurcharge = !hotelAvailable ? 0 : (dayRate.extraGuestSurcharge ?? 0)
        let roomPrices = dayRate.roomsPrices ?? []
        if (isPriceTaxExclusive && roomPrices.length > 0) {
          const taxPerRoom = taxesAndFees / roomPrices.length
          roomPrices = roomPrices.map(price => price - taxPerRoom)
        }
        return {
          uniqueKey,
          day: day.day,
          month: monthText,
          year: day.year.toString(),
          monthKey,
          checkIn: day.checkIn,
          checkOut: day.checkOut,
          canRequestDates,
          taxesAndFees,
          propertyFees,
          roomPrices,
          hotelPrice,
          hotelValue: !hotelAvailable ? 0 : dayRate.accommodationValue ?? 0,
          hotelTotal: hotelPrice + surcharge + extraGuestSurcharge + propertyFees,
          packagedTotal: hotelPrice + surcharge + extraGuestSurcharge + flightPrice + propertyFees,
          extraGuestSurcharge: extraGuestSurcharge ?? 0,
          flightPrice,
          surcharge,
          hotelAvailable,
          flightCacheActive,
          roomRateId: dayRate.roomRateId,
          // @ts-ignore: should the whole function return an empty list when
          // offerPackage is undefined?
          roomId: offerPackage.roomType.id,
          flightsAvailable: dateIsBefore(new Date(day.checkOut), maxFlightDate),
          availableRooms: dayRate.availableRooms,
        }
      })

      return {
        uniqueKey,
        key: monthKey,
        month: monthText,
        year: prices[0].year.toString(),
        monthIndex,
        yearNum: prices[0].year,
        days,
        cheapestDay: min(days.filter(d => d.hotelAvailable), d => d.hotelTotal),
        cheapestDayWithFlights: min(days.filter(d => d.flightsAvailable && d.hotelAvailable), d => d.packagedTotal),
        hotelAvailable: days.some(d => d.hotelAvailable),
        canRequestDates: days.some(d => d.canRequestDates),
        flightsAvailable: days.some(d => d.flightsAvailable && d.hotelAvailable),
        flightCacheActive,
      }
    })

    return {
      packageId: serverCalendar.packageId,
      uniqueKey,
      months,
      // @ts-ignore: Array.filter removes all undefined days
      cheapestDay: min(months.map(m => m.cheapestDay).filter(Boolean), d => d.hotelTotal),
      // @ts-ignore: Array.filter removes all undefined days
      cheapestDayWithFlights: min(months.map(m => m.cheapestDayWithFlights).filter(Boolean), d => d.packagedTotal),
      flightCacheActive,
      flightsNotAvailable,
    }
  })
}

interface CalendarParams {
  flightOrigin?: string;
  flightProvider?: string;
  timezoneOffset?: string;
  minDate?: string;
  packages: Array<App.Package>;
  occupants: Array<App.Occupants>;
  enquiryType?: 'customer' | 'admin'
}

export function getCalendars(
  offer: App.Offer | App.OfferSummary,
  region: string,
  params: CalendarParams = { occupants: [], packages: [], enquiryType: 'customer' },
) {
  // server expects 1 query per duration combination
  const queryGroups = groupBy(params.packages, pkg => pkg.duration)

  return Promise.all([...queryGroups.entries()].map(([duration, pkgGroup]) => {
    const query = qs.stringify({
      origin: params.flightOrigin,
      region,
      number_of_nights: duration,
      occupancy: params.occupants.map(generateOccupancyStringByRoom),
      provider: params.flightProvider || getProviders(),
      min_date: params.minDate,
      timezone_offset: params.timezoneOffset ?? 0,
      package_ids: pkgGroup.map((pkg) => pkg.id).join(','),
      enquiry_type: params.enquiryType,
    })

    // compression in header for fix the compression on backend side
    // TODO: after fix remove it and use Accept-Encoding
    return request.get<App.ApiResponse<Array<ServerCalendar>>>(`/api/calendar/${offer.id}/rates?${query}`, { headers: { compression: 'gzip' } })
      .then(resp => resp.result.flatMap(serverCalendar => mapCalendar(
        serverCalendar,
        params.packages,
        duration,
        offer,
        region,
      )))
  })).then((calendarArrays) => calendarArrays.flat())
}

interface LowestPriceQueryParams {
  region: string;
  currencyCode: string;
  propertyTimezone?: string;
  checkIn: string;
  checkOut: string;
  rooms: Array<App.Occupants>
}

export async function getLowestPriceForOffer(offerId: string, params: LowestPriceQueryParams): Promise<App.OfferMaybeAvailableRate> {
  const queryParams = qs.stringify({
    region: params.region,
    check_in: params.checkIn,
    check_out: params.checkOut,
    occupancy: params.rooms.map(generateOccupancyStringByRoom),
  })

  try {
    // compression in header for fix the compression on backend side
    // TODO: after fix remove it and use Accept-Encoding
    const response = await request.get<App.ApiResponse<ServerAvailablePrice>>(`/api/calendar/${offerId}/lowest-prices?${queryParams}`, { headers: { compression: 'gzip' } })

    if (response.status === 404) {
      // Offer has no packages available with these parameters.
      // The API still returns a 200 response in this case, which is wrong and should be fixed,
      // but in the meantime we need to handle it here.
      return { available: false }
    }

    const rate = availablePriceMap(response.result, {
      timezone: params.propertyTimezone ?? 'UTC',
      regionCode: params.region,
      currencyCode: params.currencyCode,
    })
    return ({
      available: true,
      rate,
    })
  } catch (error) {
    if (error && typeof error === 'object' && 'status' in error &&
        (error.status === 404 || error.status === 422)) {
      // Offer has no packages available with these parameters
      return { available: false }
    }
    throw error
  }
}

interface AvailabilityQueryParams {
  region: string;
  currencyCode: string;
  propertyTimezone?: string;
  checkIn: string;
  checkOut: string;
  rooms: Array<App.Occupants>
}

export function getAvailableRatesForOffer(offerId: string, params: AvailabilityQueryParams) {
  const queryParams = qs.stringify({
    region: params.region,
    check_in: params.checkIn,
    check_out: params.checkOut,
    occupancy: params.rooms.map(generateOccupancyStringByRoom),
  })

  // compression in header for fix the compression on backend side
  // TODO: after fix remove it and use Accept-Encoding
  return request.get<App.ApiResponse<Array<ServerAvailablePrice>>>(`/api/calendar/${offerId}/availability?${queryParams}`, { headers: { compression: 'gzip' } })
    .then(resp => resp.result.map(result => availablePriceMap(result, {
      timezone: params.propertyTimezone ?? 'UTC',
      regionCode: params.region,
      currencyCode: params.currencyCode,
    })))
}

interface getTaxBreakdownParams {
  offerId: string
  packageId: string
  propertyId: string
  roomTypeId: string
  roomRateId: string
  checkIn: string
  checkOut: string
  occupancies: string[]
  region: string
  nights: number
  enquiryType: string
  currency_code: string
  origin: string
}

export const getTaxBreakdown = async(
  {
    offerId,
    packageId,
    propertyId,
    roomTypeId,
    roomRateId,
    checkIn,
    checkOut,
    occupancies,
    region,
    nights,
    enquiryType,
    currency_code,
    origin,
  }: getTaxBreakdownParams,
) => {
  let taxBreakdownPerOccupancy: { occupancy: string; taxBreakdown: App.TaxBreakdown[]; }[] = []
  try {
    const promises = occupancies.map((occupancy) =>
      request
        .get<App.ApiResponse<ServerTaxBreakdown>>(
          `/api/v2/calendar/${offerId}/${packageId}/rate-taxes?${qs.stringify({
            property_id: propertyId,
            room_type_id: roomTypeId,
            room_rate_id: roomRateId,
            check_in: checkIn,
            check_out: checkOut,
            occupancy,
            region,
            nights,
            enquiry_type: enquiryType,
            currency_code,
            origin,
          })}`,
        )
        .then((resp) => ({
          occupancy,
          taxBreakdown: resp.result.tax_breakdown.map(taxBreakdownMap),
        })),
    )
    taxBreakdownPerOccupancy = await Promise.all(promises)
  } catch (error) {
    if (error && typeof error === 'object' && 'status' in error &&
        (error.status === 404 || error.status === 422)) {
      // NO taxBreakdown is available
      return {}
    }
    throw error
  }
  return taxBreakdownPerOccupancy.reduce((acc, mappedTaxBreakdown) => ({ ...acc, [mappedTaxBreakdown.occupancy]: mappedTaxBreakdown.taxBreakdown }), {})
}

export function getCalendarsByOccupancy(
  offer: App.Offer | App.OfferSummary,
  region: string,
  params: CalendarParams,
) {
  return Promise.all(params.occupants.map(occupancy => {
    return getCalendars(offer, region, { ...params, occupants: [occupancy] })
      .then(calendars => ({
        // Returns an object with:
        //  - "occupancy as string" as key
        //  - an object similar to the "calendarsByPackageKey" as value
        // @ts-ignore: why is not uniqueKey a required field?
        [generateOccupancyStringByRoom(occupancy)]: arrayToObject(calendars, (pkgCalendar) => pkgCalendar.uniqueKey),
      }))
  }))
    // Returns all the "calendars objects" merged into a single one
    .then(mergeObjects)
}
